// frontend/src/config/nav.ts — the ONE declarative navigation registry.
//
// Single source of truth for: the top-bar domains, the admin-section sidebar,
// the gear's visibility, and the "first allowed admin section" landing. dev's
// failure mode was three drifting gating lists (Navbar / AdminLayout /
// AdminRedirect) that disagreed; deriving every menu + every gate from this one
// list makes disagreement structurally impossible.
//
// THE RULE: a menu entry shows when you hold a key for it, or have something in
// it — never to every member because a page answers an empty list. Each entry's
// `gate` is typed against the capability catalogue (@ems-app/schemas/permissions)
// — a key, or ANY of a family's keys derived from the catalogue (`keysOf`, never
// a hand-written list) — or one of the synthetic atoms `useCan` evaluates:
// 'member' (Home), 'admin' (built-ins with no key — project settings), and the
// "something in it" atoms read off `/me`: 'on-a-ticket' · 'on-a-team' (the
// person's own tickets and teams — the reads' participation and membership arms,
// asked of the person) · 'namer:<level>' (the people key with that
// responsibility level — `namer:team` reads the teams it may name, ADR-083 §3;
// `namer:site` names a site's chains). An AREA domain (Work) carries no gate of
// its own: it shows when its rail shows an entry, and lands on the first one.
//
// The gate is permission AND entitlement: an entry tagged with an `entitlement`
// is shown only when that optional-module flag is on too (ANATOMY L7 — easy to
// miss). The ON-set rides `/me` per project (`useEntitlements`, ADR-044) and the
// shells pass it into the NavContext. A catalogue-key `gate` already carries its
// own entitlement check inside `useCan` (ADR-044); the explicit `entitlement`
// field gates the synthetic atoms and is defense in depth. The AND is structural
// + unit-tested.

import type { Icon } from '../components/ui/icons.js';
import { icons } from '../components/ui/icons.js';
import { PROJECT_GRANTABLE_KEYS, type PermissionKey, type EntitlementFlag } from '@ems-app/schemas/permissions';
import type { ResponsibilityLevel } from '@ems-app/schemas/responsibilities/levels';

/** A single gate atom — a catalogue key, or a synthetic atom (see the header). The unit `useCan`/`can` evaluates. */
export type NavGate = PermissionKey | 'member' | 'admin' | 'on-a-ticket' | 'on-a-team' | `namer:${ResponsibilityLevel}`;

/** The rails an area domain folds — the in-area left navs (and the phone drawer's disclosures). */
export type AreaGroup = 'assets' | 'work';

/**
 * A nav ENTRY's gate: a single atom, OR an ANY-of set (the OR-gate — shows if the
 * viewer passes ANY: the dual-audience entries, e.g. zones reachable by
 * `reference_data:manage` OR `users:manage`; a family's keys; a key OR something
 * in it), OR an AREA (shows when that rail shows an entry). Only `NavItem.gate`
 * uses the compounds; `NavContext.can` still evaluates a single atom
 * (isNavItemVisible resolves them).
 */
export type NavItemGate = NavGate | { readonly any: readonly NavGate[] } | { readonly area: AreaGroup };

/** A family's project keys, derived from the catalogue by prefix — a new key of the family joins its gate by itself. */
function keysOf(prefix: string): PermissionKey[] {
  return PROJECT_GRANTABLE_KEYS.filter((k) => k.startsWith(prefix));
}

/**
 * Admin sections — a UX grouping for the admin nav, independent of the per-item
 * permission gate (grouping ⊥ gating).
 *
 * The split is "vocabularies you define" vs "the project itself":
 *   'referenceData' — the taxonomies this project authors (responsibilities,
 *                     zones · clusters · administrative areas, the site
 *                     vocabularies). Responsibilities (entitlement-gated on
 *                     has_field_force) leads it.
 *   'project'       — the project as an object: its identity + defaults, its
 *                     audit trail, its import history, its API.
 * Teams join People when the teams entity lands (under design — no stub before). Tunable
 * as the admin surface grows.
 */
export const ADMIN_SECTION_ORDER = ['people', 'referenceData', 'contract', 'project'] as const;
export type AdminSection = (typeof ADMIN_SECTION_ORDER)[number];

export interface NavItem {
  /** Stable id (test + key) — never user-facing. */
  readonly id: string;
  /** Key within the `nav` i18n namespace (e.g. 'domains.sites'). */
  readonly labelKey: string;
  /** Path under `/p/:projectId/` ('' = the home index); an absolute URL when `external`. */
  readonly route: string;
  /** Renders as an external `<a target="_blank">` (e.g. the backend Swagger UI) — `route` is the href. */
  readonly external?: boolean;
  readonly icon: Icon;
  readonly group: 'primary' | 'admin' | 'assets' | 'work';
  readonly gate: NavItemGate;
  /** Admin-only — which sidebar section this item is grouped under. */
  readonly section?: AdminSection;
  /** Second half of the gate — shown only when this module flag is also on. */
  readonly entitlement?: EntitlementFlag;
  /** Not yet live (a later phase lands the page): both the area rail and the
   *  hamburger render the entry as a "coming soon" toast button, never a link. */
  readonly soon?: boolean;
  readonly order: number;
}

// Primary domains (top bar) + admin sections (behind the gear). Order is the
// `order` field, not array position, so reordering is a one-number edit.
export const NAV_ITEMS: readonly NavItem[] = [
  // ── Primary domains ──────────────────────────────────────────────────────
  { id: 'home',       labelKey: 'domains.home',       route: '',           icon: icons.home,    group: 'primary', gate: 'member',   order: 10 },
  { id: 'monitoring', labelKey: 'domains.monitoring', route: 'monitoring', icon: icons.monitoring,    group: 'primary', gate: 'monitoring:read', order: 20 },
  // Work — the field work's area (the tickets hub, the teams — ADR-083 §11; the
  // module keeps its own UI name, Field force), right after Monitoring
  // (architect-ruled 2026-10-15), behind the field-force module: it shows when
  // its rail shows an entry and lands on the first (`domainRoute`).
  { id: 'work',       labelKey: 'domains.work',       route: 'tickets',    icon: icons.maintenance, group: 'primary', gate: { area: 'work' }, entitlement: 'has_field_force', order: 25 },
  { id: 'sites',      labelKey: 'domains.sites',      route: 'sites',      icon: icons.site,   group: 'primary', gate: 'site:read',  order: 30 },
  { id: 'assets',     labelKey: 'domains.assets',     route: 'assets',     icon: icons.asset,     group: 'primary', gate: 'asset:read', order: 40 },
  // Reports — any report key (one standalone toggle per report; seeing a report is exporting it).
  { id: 'reports',    labelKey: 'domains.reports',    route: 'reports',    icon: icons.report, group: 'primary', gate: { any: keysOf('report:') },   order: 60 },

  // ── The Assets AREA rail (group 'assets') — the in-area left nav, same
  //    registry so the rail and the hamburger can't disagree (the admin-sidebar
  //    lesson). Order is architect-ruled (2026-08-08): Register first, Devices
  //    second (live: the connectivity record over the one register),
  //    then Storage (the word re-decided when the warehouses & fleet module
  //    lands) · Fleet · Consumables · Catalog · Reconciliation. `soon` entries
  //    toast until their module lands (Storage/Fleet = warehouses & fleet ·
  //    Consumables = the transaction verbs · Reconciliation = the
  //    reconciliation engine, ADR-064).
  //    Storage and Fleet already wear their real gates — a right on the place
  //    opens the place itself: depots ride `warehouse:read`, the fleet
  //    `vehicle:read` with the vehicles module on.
  { id: 'assets-register',       labelKey: 'assetsArea.register',       route: 'assets',  icon: icons.asset,      group: 'assets', gate: 'asset:read', order: 10 },
  { id: 'assets-devices',        labelKey: 'assetsArea.devices',        route: 'assets/devices',  icon: icons.devices, group: 'assets', gate: 'asset:read', order: 20 },
  { id: 'assets-storage',        labelKey: 'assetsArea.storage',        route: 'assets',  icon: icons.warehouse,  group: 'assets', gate: 'warehouse:read', soon: true, order: 30 },
  { id: 'assets-fleet',          labelKey: 'assetsArea.fleet',          route: 'assets',  icon: icons.vehicle,    group: 'assets', gate: 'vehicle:read', entitlement: 'has_vehicles', soon: true, order: 40 },
  { id: 'assets-consumables',    labelKey: 'assetsArea.consumables',    route: 'assets',  icon: icons.package,    group: 'assets', gate: 'asset:read', soon: true, order: 50 },
  { id: 'assets-catalog',        labelKey: 'assetsArea.catalog',        route: 'assets/catalog',  icon: icons.categories, group: 'assets', gate: 'asset:read', order: 60 },
  { id: 'assets-reconciliation', labelKey: 'assetsArea.reconciliation', route: 'assets',  icon: icons.recheck,    group: 'assets', gate: 'asset:read', soon: true, order: 70 },

  // ── The Work AREA rail (group 'work') — the same registry, so the rail and
  //    the hamburger's Work disclosure can't disagree: the tickets hub (a key on
  //    any ticket class, or a ticket you are on), then the Teams (a team key, the
  //    people key with the team level, or a team you are on — ADR-083 §3 · §11). The
  //    visits log and planning join the rail at their phases, never as stubs.
  { id: 'work-hub',              labelKey: 'workArea.hub',              route: 'tickets', icon: icons.ticket,     group: 'work', gate: { any: [...keysOf('ticket.'), 'on-a-ticket'] }, entitlement: 'has_field_force', order: 10 },
  { id: 'work-teams',            labelKey: 'workArea.teams',            route: 'teams',   icon: icons.team,       group: 'work', gate: { any: ['team:read', 'namer:team', 'on-a-team'] }, entitlement: 'has_field_force', order: 20 },

  // ── Admin sections (behind the gear) — grouped; `order` is global so the flat
  //    sort (gear landing) follows section order: People < Reference data <
  //    Contract < Project. Section grouping ⊥ per-item gate (e.g. Responsibilities =
  //    reference_data:manage under Reference data).
  // People
  { id: 'users',               labelKey: 'admin.users',              route: 'users',               icon: icons.users,                 group: 'admin', section: 'people',        gate: 'users:manage',             order: 10 },
  { id: 'roles',               labelKey: 'admin.roles',              route: 'roles',               icon: icons.role,           group: 'admin', section: 'people',        gate: 'users:manage',             order: 30 },
  // Reference data — the vocabularies this project authors. Responsibilities
  //   (field-force-entitled) leads, then the rest (all reference_data:manage).
  { id: 'responsibilities',    labelKey: 'admin.responsibilities',   route: 'responsibilities',    icon: icons.responsibilities,         group: 'admin', section: 'referenceData',       gate: 'reference_data:manage',    entitlement: 'has_field_force', order: 45 },
  // Zones & clusters — the two territory levels, one tabbed page (the Site fields fold).
  { id: 'zones-clusters',      labelKey: 'admin.zonesClusters',      route: 'zones-clusters',      icon: icons.cluster,         group: 'admin', section: 'referenceData',        gate: { any: ['reference_data:manage', 'users:manage'] }, order: 50 },
  { id: 'administrative-areas',         labelKey: 'admin.administrativeAreas',        route: 'administrative-areas',         icon: icons.administrativeArea,                 group: 'admin', section: 'referenceData',        gate: 'reference_data:manage',    order: 70 },
  // The four site vocabularies (types · priorities · categories · tags) fold into
  // ONE deep-linkable tabbed page — the same fold the contract-terms
  // reference surface uses. One sidebar row; the tabs live on the page.
  { id: 'site-fields',         labelKey: 'admin.siteFields',         route: 'site-fields',         icon: icons.siteFields,           group: 'admin', section: 'referenceData',        gate: 'reference_data:manage',    order: 80 },
  // Asset owners — the owners a unit's books point at (pooled across projects).
  { id: 'asset-owners',        labelKey: 'admin.assetOwners',        route: 'asset-owners',        icon: icons.assetOwner,           group: 'admin', section: 'referenceData',        gate: 'reference_data:manage',    order: 85 },
  // Contract — three sidebar rows; Contract TERMS deliberately has NO row (it
  // changes ~once a year): its door is the Configurations page's ⋯ menu, the
  // route stays deep-linkable.
  { id: 'configurations',      labelKey: 'admin.configurations',     route: 'configurations',      icon: icons.invoicing,               group: 'admin', section: 'contract',      gate: 'contractual_config:manage', entitlement: 'has_invoicing', order: 112 },
  // The contract's REFERENCE surface — terms · bands · commercial flags as tabs
  // on ONE deep-linkable page (configurations, the working collection, stays its
  // own row; everything the contract established folds here).
  { id: 'contract-terms',      labelKey: 'admin.contractTerms',      route: 'contract-terms',      icon: icons.terms,                   group: 'admin', section: 'contract',      gate: 'contractual_config:manage', entitlement: 'has_invoicing', order: 114 },
  // Project — the project as an object: identity + defaults, audit, imports, API.
  { id: 'settings',            labelKey: 'admin.projectSettings',    route: 'settings',            icon: icons.adjust,               group: 'admin', section: 'project',        gate: 'admin',                    order: 120 },
  // Files — the review policy + the file numbers, its own page under Project
  // settings (architect-ruled; a shortcut from the library may come later).
  { id: 'settings-files',      labelKey: 'admin.filesSettings',      route: 'settings/files',      icon: icons.file,                 group: 'admin', section: 'project',        gate: 'admin',                    order: 121 },
  // Field work — the visit numbers (the on-site tolerance · the nearby radius ·
  // the presumed end of a forgotten visit): a module's policy follows the module.
  { id: 'settings-field',      labelKey: 'admin.fieldSettings',      route: 'settings/field',      icon: icons.site,                 group: 'admin', section: 'project',        gate: 'admin',                    entitlement: 'has_field_force', order: 122 },
  { id: 'integrations',        labelKey: 'admin.integrations',        route: 'integrations',        icon: icons.integration,          group: 'admin', section: 'project',        gate: 'admin',                    order: 125 },
  { id: 'audit-log',           labelKey: 'admin.auditLog',           route: 'audit-log',           icon: icons.history, group: 'admin', section: 'project',        gate: 'audit:read',               order: 130 },
  // Imports history — Tier-2 of the audit model, so it shares the audit gate.
  { id: 'imports',             labelKey: 'admin.imports',            route: 'imports',             icon: icons.upload,  group: 'admin', section: 'project',        gate: 'audit:read',               order: 131 },
  { id: 'api-docs',            labelKey: 'admin.apiDocs',            route: '/api/docs', external: true, icon: icons.code,        group: 'admin', section: 'project',        gate: 'api_docs:read',            order: 140 },
];

/** The decision context the pure gating functions evaluate against. */
export interface NavContext {
  /** From `useCan(projectId)` — evaluates a single atom; compound (any-of) gates are resolved in isNavItemVisible. */
  readonly can: (gate: NavGate) => boolean;
  /** Optional-module flags that are ON for this project. */
  readonly entitlements: ReadonlySet<EntitlementFlag>;
}

/**
 * Empty entitlement set — the fallback for a context with no project, and the
 * convenient default in tests / the Workbench. Production shells feed the real
 * ON-set via `useEntitlements` (ADR-044); with an empty set, entitlement-gated
 * entries stay hidden (the safe default).
 */
export const NO_ENTITLEMENTS: ReadonlySet<EntitlementFlag> = new Set();

/** Permission AND entitlement — the full gate for one entry. */
export function isNavItemVisible(item: NavItem, ctx: NavContext): boolean {
  if (item.entitlement && !ctx.entitlements.has(item.entitlement)) return false;
  const gate = item.gate;
  if (typeof gate !== 'object') return ctx.can(gate);
  if ('any' in gate) return gate.any.some((g) => ctx.can(g));
  return visibleNavItems(gate.area, ctx).length > 0;
}

/** Visible items of a group, in `order`. */
export function visibleNavItems(group: NavItem['group'], ctx: NavContext): NavItem[] {
  return NAV_ITEMS.filter((i) => i.group === group && isNavItemVisible(i, ctx)).sort(
    (a, b) => a.order - b.order,
  );
}

/** Where a domain's link lands — its own route, or for an AREA domain the first entry its rail shows (a person who
 *  sees only the Teams lands on the Teams, never on an empty hub). */
export function domainRoute(item: NavItem, ctx: NavContext): string {
  const gate = item.gate;
  if (typeof gate === 'object' && 'area' in gate) return visibleNavItems(gate.area, ctx)[0]?.route ?? item.route;
  return item.route;
}

/** Gear shows iff the user can reach ≥1 admin section (a sysadmin's gear adds the platform door — `Gear`). */
export function gearVisible(ctx: NavContext): boolean {
  return visibleNavItems('admin', ctx).length > 0;
}

/**
 * The "first allowed admin section" landing — the route segment the gear
 * navigates to. null when no admin section is reachable (gear hidden).
 */
export function firstAllowedAdminRoute(ctx: NavContext): string | null {
  return visibleNavItems('admin', ctx)[0]?.route ?? null;
}

/**
 * Visible admin items grouped by section, in section order — only sections with
 * ≥1 permitted item appear. Drives the desktop admin sidebar + the mobile
 * hamburger's admin block (same source → they can't disagree).
 */
export function visibleAdminSections(
  ctx: NavContext,
): { section: AdminSection; labelKey: string; items: NavItem[] }[] {
  const visible = visibleNavItems('admin', ctx);
  return ADMIN_SECTION_ORDER.map((section) => ({
    section,
    labelKey: `admin.sections.${section}`,
    items: visible.filter((i) => i.section === section),
  })).filter((g) => g.items.length > 0);
}

/** Whether a path stands on a domain's ground — the domain's own route or one of its AREA rail's routes (the Work
 *  area's pages are not under a `/work` segment: the hub lives at `tickets`, the teams at `teams`), so the top bar
 *  and the phone drawer light the same way from the one registry. Segment-aware; the home route matches exactly. */
export function isOnDomain(projectId: string, item: NavItem, pathname: string): boolean {
  if (item.route === '') return pathname === navItemPath(projectId, '');
  const routes = [item.route, ...NAV_ITEMS.filter((i) => i.group === item.id).map((i) => i.route)];
  return routes.some((r) => {
    const base = navItemPath(projectId, r);
    return pathname === base || pathname.startsWith(`${base}/`);
  });
}

/** Absolute path for an item under the current project. '' → the home index. */
export function navItemPath(projectId: string, route: string): string {
  return `/p/${projectId}/${route}`;
}

// ── Platform nav (sysadmin cross-project context) ──────────────────────────
// The platform context (`/platform/*`, ADR-018 sysadmin trust zone) gets its
// own top-bar tabs, parallel to the project domains. Absolute routes (not
// project-relative) and gated purely by `is_sysadmin` — the platform context is
// only reachable by a sysadmin, so there's no per-item permission key. Today
// the pages are ComingSoon stubs; the real platform portal lands at its slice.
export interface PlatformNavItem {
  readonly id: string;
  readonly labelKey: string; // `nav` namespace key, e.g. 'platform.projects'
  readonly route: string; // segment under /platform/
  readonly icon: Icon;
}

export const PLATFORM_NAV: readonly PlatformNavItem[] = [
  { id: 'platform-projects', labelKey: 'platform.projects', route: 'projects', icon: icons.building },
  // Products second (architect-ruled) — the catalog concept keeps its one
  // glyph app-wide (the Assets-rail entry); the route keeps the concept word.
  { id: 'platform-catalog', labelKey: 'platform.catalog', route: 'catalog', icon: icons.categories },
  { id: 'platform-users', labelKey: 'platform.users', route: 'users', icon: icons.users },
  { id: 'platform-sso', labelKey: 'platform.sso', route: 'sso', icon: icons.password },
  { id: 'platform-api-clients', labelKey: 'platform.apiClients', route: 'api-clients', icon: icons.apiClient },
  // No API-docs entry here: the single admin-sidebar "API" item (api_docs:read)
  // reaches /api/docs, and its spec picker exposes the internal portal to a
  // sysadmin — a platform-context entry would be a redundant second door.
];

/** Absolute path for a platform item. */
export function platformNavPath(route: string): string {
  return `/platform/${route}`;
}

/** Where "Platform settings" lands — the gear's platform door for a sysadmin (the Gear · the phone drawer). */
export const PLATFORM_HOME = platformNavPath('projects');
